import React from 'react';
import PropTypes from 'prop-types';
import { CustomInput, Input } from 'reactstrap';

/**
 * Shared venue/layout assignment controls for a POS station.
 * Layouts for the selected venue must be passed in — parent is responsible
 * for eagerly loading them so existing account layouts are always selectable.
 */
export default function StationLayoutBindingControls({
    stationId,
    selection,
    venues,
    layouts,
    onChange,
    onSave,
    t,
    className,
    showSave,
}) {
    const sel = selection || {
        EnableLayoutMode: false,
        PosVenueId: null,
        PosLayoutId: null,
        saving: false,
    };
    const activeVenues = (venues || []).filter((v) => v.IsActive);
    const venueLayouts = (layouts || []).filter((l) => l.IsActive !== false);
    const hasVenue = !!sel.PosVenueId;
    const emptyLayouts = hasVenue && venueLayouts.length === 0;

    return (
        <div className={className || ''}>
            <CustomInput
                type="switch"
                id={`layoutmode-station-${stationId}`}
                label={sel.EnableLayoutMode ? t('layouts.layoutOn') : t('layouts.layoutOff')}
                checked={!!sel.EnableLayoutMode}
                onChange={(e) => onChange('EnableLayoutMode', e.target.checked)}
            />
            <Input
                type="select"
                bsSize="sm"
                className="mt-2"
                value={sel.PosVenueId || ''}
                onChange={(e) => onChange(
                    'PosVenueId',
                    e.target.value ? parseInt(e.target.value, 10) : null,
                )}
            >
                <option value="">{t('layouts.venueOption')}</option>
                {activeVenues.map((v) => (
                    <option key={v.VenueId} value={v.VenueId}>{v.Name}</option>
                ))}
            </Input>
            <Input
                type="select"
                bsSize="sm"
                className="mt-2"
                value={sel.PosLayoutId || ''}
                disabled={!hasVenue}
                onChange={(e) => onChange(
                    'PosLayoutId',
                    e.target.value ? parseInt(e.target.value, 10) : null,
                )}
            >
                <option value="">{t('layouts.layoutOption')}</option>
                {venueLayouts.map((l) => (
                    <option key={l.LayoutId} value={l.LayoutId}>{l.Name}</option>
                ))}
            </Input>
            {emptyLayouts ? (
                <p className="text-muted small mt-1 mb-0">{t('layouts.noLayoutsForVenue')}</p>
            ) : null}
            {showSave ? (
                <button
                    type="button"
                    className="btn btn-sm btn-primary mt-2"
                    disabled={!!sel.saving}
                    onClick={onSave}
                >
                    {sel.saving ? t('common.saving') : t('layouts.saveLayoutBinding')}
                </button>
            ) : null}
        </div>
    );
}

StationLayoutBindingControls.propTypes = {
    stationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    selection: PropTypes.object,
    venues: PropTypes.array,
    layouts: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    t: PropTypes.func.isRequired,
    className: PropTypes.string,
    showSave: PropTypes.bool,
};

StationLayoutBindingControls.defaultProps = {
    selection: null,
    venues: [],
    layouts: [],
    onSave: undefined,
    className: '',
    showSave: true,
};
